import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { dueBadge, moneyDisplay } from "@icraftx/shared/v1";
import { Button } from "@/components/ui/button";
import { AxisProgress } from "@/modules/core/components/AxisProgress";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import {
  DueBadge,
  FilterPartyName,
} from "@/modules/core/components/DueBadge";
import {
  togglePartyFilter,
  type PartyFilter,
} from "@/modules/core/list-cell-actions";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import type { ToCollectRow } from "@/shared/api/sales";

const columnHelper = createColumnHelper<DeskTableFeatures, ToCollectRow>();

export type ToCollectCellActions = {
  partyFilter: PartyFilter | null;
  setPartyFilter: (next: PartyFilter | null) => void;
};

export function toCollectColumns(
  companySlug: string,
  actions: ToCollectCellActions,
) {
  return columnHelper.columns([
    columnHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 120,
      cell: (info) => {
        const row = info.row.original;
        const to =
          row.kind === "sales_invoice"
            ? `/c/${companySlug}/sales/invoices/${row.id}`
            : `/c/${companySlug}/sales/orders/${row.id}`;
        return <DocNumberLink to={to}>{info.getValue()}</DocNumberLink>;
      },
    }),
    columnHelper.accessor("customerName", {
      id: "customerName",
      header: "Customer",
      size: 180,
      cell: (info) => {
        const row = info.row.original;
        return (
          <FilterPartyName
            name={info.getValue()}
            onClick={() =>
              actions.setPartyFilter(
                togglePartyFilter(actions.partyFilter, {
                  id: row.customerId,
                  name: row.customerName,
                }),
              )
            }
          />
        );
      },
    }),
    columnHelper.display({
      id: "payPct",
      header: "Paid",
      size: 130,
      enableSorting: false,
      meta: { align: "right" },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <AxisProgress
            axis="Paid"
            format="money"
            current={r.amountPaid}
            max={r.total || r.grossTotal}
          />
        );
      },
    }),
    columnHelper.accessor("outstanding", {
      id: "outstanding",
      header: "Outstanding",
      size: 120,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("paymentDueOn", {
      id: "paymentDueOn",
      header: "Due",
      size: 120,
      cell: (info) => {
        const badge = dueBadge(info.getValue());
        return badge.label ? <DueBadge badge={badge} /> : "—";
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 96,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const r = row.original;
        const to =
          r.kind === "sales_invoice"
            ? `/c/${companySlug}/sales/invoices/${r.id}`
            : `/c/${companySlug}/sales/orders/${r.id}`;
        return (
          <Button variant="outline" asChild size="sm">
            <Link to={to} className="no-underline">
              Collect
            </Link>
          </Button>
        );
      },
    }),
  ]);
}
